import { Snackbar } from "@mui/material";
import Slide, { SlideProps } from "@mui/material/Slide";
import { styled } from "@mui/material/styles";
import {
	FC,
	PropsWithChildren,
	ReactElement,
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";

function SlideTransition(props: PropsWithChildren<SlideProps>) {
	return (
		<Slide {...props} direction="down">
			{props.children}
		</Slide>
	);
}

export type SnackbarContextType = {
	openSnackbarWithContent: (content: ReactElement) => void;
};

const StyledSnackbar = styled(Snackbar)(({ theme }) => ({
	padding: "20px 40px",
	width: "95%",
	margin: "auto",
	backgroundColor: theme.palette.common.white,
	borderRadius: theme.shape.borderRadius,
}));

export const SnackbarContext = createContext<SnackbarContextType>({} as SnackbarContextType);

type QueuedSnackbar = {
	id: number;
	content: ReactElement;
};

export const SnackbarContextProvider: FC<PropsWithChildren> = ({ children }) => {
	const [open, setOpen] = useState(false);
	const [snackbarQueue, setSnackbarQueue] = useState<readonly QueuedSnackbar[]>([]);
	const [currentSnackbar, setCurrentSnackbar] = useState<QueuedSnackbar | undefined>(undefined);
	const snackbarIdRef = useRef(0);

	useEffect(() => {
		if (snackbarQueue.length && !currentSnackbar) {
			// Set a new snack when we don't have an active one
			setCurrentSnackbar(snackbarQueue[0]);
			setSnackbarQueue((prev) => prev.slice(1));
			setOpen(true);
		} else if (snackbarQueue.length && currentSnackbar && open) {
			// Close an active snack when a new one is added
			setOpen(false);
		}
	}, [snackbarQueue, currentSnackbar, open]);

	const openSnackbarWithContent = (content: ReactElement) => {
		const id = snackbarIdRef.current;
		snackbarIdRef.current += 1;
		setSnackbarQueue((prev) => [...prev, { id, content }]);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleExited = () => {
		setCurrentSnackbar(undefined);
	};

	return (
		<SnackbarContext.Provider value={{ openSnackbarWithContent }}>
			<StyledSnackbar
				key={currentSnackbar?.id}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				autoHideDuration={5000}
				open={open}
				onClose={handleClose}
				slots={{
					transition: SlideTransition,
				}}
				slotProps={{
					transition: { onExited: handleExited },
				}}
			>
				{currentSnackbar?.content}
			</StyledSnackbar>
			{children}
		</SnackbarContext.Provider>
	);
};

export const useSnackbarContext = () => {
	return useContext(SnackbarContext);
};
